<template>
  <div
    class="hidden md:flex max-w-[1280px] mx-auto px-6 h-16 items-center justify-between"
  >
    <!-- Logo -->
    <Link :href="navData.rootPath" class="flex items-center gap-2.5 shrink-0">
      <EmmalagatLogo :size="48" class="cursor-pointer" />
    </Link>

    <!-- Nav Links -->
    <div class="flex items-center gap-8">
      <Link
        v-for="link in navLinks"
        :key="link.href"
        :href="link.href"
        class="relative text-[15px]"
        :class="
          isActive(link.href)
            ? 'font-semibold text-[#191c1e]'
            : 'font-normal text-[#3c4947]'
        "
      >
        {{ link.label }}
        <span
          v-if="isActive(link.href)"
          class="absolute bottom-[-2px] left-0 right-0 h-0.5 bg-teal-500 rounded-full"
        />
      </Link>
    </div>

    <!-- Icon Buttons + Expanding Search -->
    <div class="flex items-center gap-2">
      <!-- Expanding search input -->
      <div
        class="flex items-center overflow-hidden transition-all duration-300 ease-in-out"
        :style="
          searchOpen
            ? 'max-width: 280px; opacity: 1;'
            : 'max-width: 0; opacity: 0;'
        "
      >
        <input
          ref="searchInputRef"
          v-model="searchQuery"
          type="search"
          placeholder="Sök recept…"
          class="w-[260px] py-1.5 px-3 text-sm rounded-lg border border-[#eceef0] bg-[#f8fafa] focus:border-teal-400 focus:outline-none focus:ring-0 text-[#191c1e]"
          @keydown.enter="submitSearch"
          @keydown.esc="closeSearch"
        />
      </div>

      <button
        class="p-2 hover:bg-[#f2f4f6] rounded-full transition-colors cursor-pointer"
        @click="toggleSearch"
      >
        <X v-if="searchOpen" :size="18" class="text-[#3c4947]" />
        <Search v-else :size="18" class="text-[#3c4947]" />
      </button>
      <Link
        :href="
          navData.currentUser
            ? navData.currentUser.recipesPath
            : navData.newUserPath
        "
        class="p-2 hover:bg-[#f2f4f6] rounded-full transition-colors"
      >
        <Heart :size="18" class="text-[#3c4947]" />
      </Link>
      <button
        class="p-2 hover:bg-[#f2f4f6] rounded-full transition-colors cursor-pointer"
        @click="$emit('open-drawer')"
      >
        <User :size="18" class="text-[#3c4947]" />
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, nextTick } from "vue";
import { Link, router, usePage } from "@inertiajs/vue3";
import EmmalagatLogo from "@/components/icons/EmmalagatLogo.vue";
import { Search, Heart, User, X } from "lucide-vue-next";

const props = defineProps({
  navData: { type: Object, required: true },
});

defineEmits(["open-drawer"]);

const page = usePage();
const searchOpen = ref(false);
const searchQuery = ref("");
const searchInputRef = ref(null);

async function toggleSearch() {
  searchOpen.value = !searchOpen.value;
  if (searchOpen.value) {
    await nextTick();
    searchInputRef.value?.focus();
  } else {
    searchQuery.value = "";
  }
}

function closeSearch() {
  searchOpen.value = false;
  searchQuery.value = "";
}

function submitSearch() {
  const query = searchQuery.value.trim();
  closeSearch();
  if (!query) {
    router.visit(props.navData.searchPath);
    return;
  }
  router.visit(
    `${props.navData.searchPath}?search=${encodeURIComponent(query)}`,
  );
}

const navLinks = computed(() => [
  { label: "Alla recept", href: props.navData.recipesPath },
  { label: "Kategorier", href: props.navData.categoriesPath },
  { label: "Tekniker & verktyg", href: props.navData.toolsPath },
]);

function isActive(href) {
  return page.url === href || page.url.startsWith(href + "/");
}
</script>
